import { useEffect, useState, useRef } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/shadcn/ui/dialog"
import { Button } from "@/components/shadcn/ui/button"
import { Input } from "@/components/shadcn/ui/input"
import { Label } from "@/components/shadcn/ui/label"
import SignaturePad, { SignaturePadRef } from "./SignaturePad"

interface AddSignatureDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (signature: { name: string; value: string }) => void
  isLoading?: boolean
  fieldLabel?: string
}

const AddSignatureDialog = ({
  open,
  onOpenChange,
  onSave,
  isLoading = false,
  fieldLabel,
}: AddSignatureDialogProps) => {
  const [name, setName] = useState("")
  const [isSignatureEmpty, setIsSignatureEmpty] = useState(true)
  const signaturePadRef = useRef<SignaturePadRef>(null)

  const handleSave = () => {
    if (!signaturePadRef.current) return

    const signatureData = signaturePadRef.current.toDataURL()
    onSave({ name: name.trim(), value: signatureData })
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setName("")
      setIsSignatureEmpty(true)
      signaturePadRef.current?.clear()
    }
    onOpenChange(isOpen)
  }

  // Consumers close the dialog after a successful save by driving `open` to
  // `false` directly, which bypasses `handleOpenChange`. Reset dialog state on
  // any close so the save-then-reopen path cannot keep `name`/`isSignatureEmpty`
  // stale and leave Save enabled on a freshly-blank pad.
  useEffect(() => {
    if (!open) {
      setName("")
      setIsSignatureEmpty(true)
      signaturePadRef.current?.clear()
    }
  }, [open])

  const isValid = name.trim().length > 0 && !isSignatureEmpty

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Signature</DialogTitle>
          {fieldLabel && <DialogDescription>Adding signature to: {fieldLabel}</DialogDescription>}
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="signer-name">Signer Name</Label>
            <Input
              id="signer-name"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Signature</Label>
            <SignaturePad
              ref={signaturePadRef}
              onSignatureChange={(isEmpty) => setIsSignatureEmpty(isEmpty)}
              width={440}
              height={200}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid || isLoading}>
            {isLoading ? "Saving..." : "Save Signature"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AddSignatureDialog
